import {
	ModifyInvoiceItemInput,
	useCreateDocumentDraftMutation,
	DocumentType,
} from "@repo/server-api";
import {
	ResultFromData,
	ResultFromError,
	ResultFromException,
	useNotificationGraphql,
} from "@repo/shared/common";
import { useTranslation } from "react-i18next";

import type { Offer } from "../types/CommercialDocumentTypes";
import type { GraphqlQueryResult } from "@repo/shared/common";
import { InvoiceItem } from "../types";

type UseCreateOfferOptions = {
	onSaved: (id: string) => void;
};

type UseCreateOfferResult = {
	save: (offer: Offer) => void;
	loading: boolean;
};

export function useCreateOfferHook(options: UseCreateOfferOptions): UseCreateOfferResult {
	const { t } = useTranslation();

	const [createOfferDraftMutation] = useCreateDocumentDraftMutation();

	const createOffer = async (offer: Offer): Promise<GraphqlQueryResult<string>> => {
		const result = await createOfferDraftMutation({
			variables: {
				documentType: DocumentType.Offer,
				clientContactPersonId: offer.contactPerson?.id || null,
				clientId: offer.client?.id || null,
				concludingText: offer.footerNote || null,
				contractorId: offer.contractor?.id || null,
				introductoryText: offer.headerNote || "",
				items: offer.items.map((item: InvoiceItem, index: number) => ({
					description: item.description,
					discountPercent: item.discount ?? 0,
					name: item.name,
					quantity: item.quantity,
					tax: item.tax,
					taxCode: item.taxCode,
					unit: item.unit,
					unitPriceNet: item.unitPrice,
					positionNo: index,
				})) as ModifyInvoiceItemInput[],
				notes: null,
				projectId: offer.project?.id || null,
				paymentDeadlineInDays: offer.paymentDeadlineDays ?? null,
				validUntil: null,
			},
		});

		if (
			result.data?.createDocumentDraft.errors &&
			result.data?.createDocumentDraft.errors.length > 0
		) {
			return ResultFromError<string>(result.data.createDocumentDraft.errors);
		}
		if (result.errors) return ResultFromException<string>(result.errors);

		const commercialDocument = result.data?.createDocumentDraft.commercialDocument;
		const offerId =
			commercialDocument && "id" in commercialDocument ? commercialDocument.id : undefined;
		if (!offerId) return ResultFromException<string>(t("no-document-id-returned"));

		return ResultFromData<string>(offerId);
	};

	const { save, loading } = useNotificationGraphql<Offer, string>({
		onComplete: (id) => {
			options.onSaved(id);
		},

		graphqlCall: createOffer,

		successMessage: t("offer-created-successfully"),
	});

	return {
		save,
		loading,
	};
}
